import {
    multiFactor,
    PhoneAuthProvider,
    PhoneMultiFactorGenerator,
    RecaptchaVerifier
} from "firebase/auth";
import { toast } from "react-toastify";
import { auth } from "../Components/firebase/firebase";

/**
 * A centralized, reusable React custom hook containing all Firebase 
 * Phone-based Multi-Factor Authentication (MFA) logic. 
 * This prevents duplicating messy verification code across Login and Signup.
 */
const useMfa = () => {

    // ============================================
    // RECAPTCHA UTILITIES
    // ============================================

    // Helper method: Connects an invisible Google reCAPTCHA widget to a specific HTML div
    const getRecaptchaVerifier = (containerId, ref) => {
        if (ref.current) return ref.current;

        try {
            ref.current = new RecaptchaVerifier(auth, containerId, {
                size: "invisible",
                'expired-callback': () => {
                    if (ref.current) {
                        toast.warn("Security check expired. Please try again.");
                    }
                }
            });
            return ref.current;
        } catch (err) {
            console.error(`Failed to initialize reCAPTCHA:`, err);
            toast.error(`Security check initialization failed: ${err.message}`);
            return null;
        }
    };

    // Helper method: Wipes out the Google reCAPTCHA safely from the website memory safely
    const resetRecaptchaVerifier = (ref, containerId) => {
        if (ref.current) {
            try {
                ref.current.clear();
            } catch (_) {
                // Swallow — may already be in a broken state
            }
            ref.current = null;
        }

        // Only wipe innerHTML — never replaceChild or removeChild on React-owned nodes
        if (containerId) {
            const container = document.getElementById(containerId);
            if (container) container.innerHTML = "";
        }
    };

    // ============================================
    // ERROR HANDLER
    // ============================================

    // Helper method: Takes complex raw Firebase errors and turns them into user-friendly Toasts
    const handleMfaError = (err, onSessionExpired, onSpecificError) => {
        console.error("MFA Process Error:", err);

        // Normalize error payloads (some Firebase errors have deeply nested strings)
        const errorMsg = (err.message || err.error?.message || "").toUpperCase();
        const errorCode = (err.code || err.error?.code || "").toString().toUpperCase();
        const errorString = JSON.stringify(err).toUpperCase() + " " + errorMsg + " " + errorCode;

        let finalMessage = "";

        // Filter and branch based on Firebase specific error codes
        if (errorString.includes('INVALID-APP-CREDENTIAL')) {
            // Configuration-level error, usually safe to ignore during routine checks
            return;
        } else if (errorString.includes('CAPTCHA-CHECK-FAILED')) {
            finalMessage = "Security check failed. Please try again.";
        } else if (
            errorString.includes('CREDENTIAL-TOO-OLD') ||
            errorString.includes('CREDENTIAL_TOO_OLD') ||
            errorString.includes('INVALID_MFA_PENDING_CREDENTIAL') ||
            errorString.includes('EXPIRED') ||
            errorString.includes('SESSION-EXPIRED') ||
            errorString.includes('SESSION_EXPIRED')
        ) {
            // The user took too long to write the SMS code. Trigger the protective kickout script inside the parent component.
            finalMessage = "Security session expired. Please sign in again.";
            if (onSessionExpired) onSessionExpired();
        } else if (errorString.includes("ALREADY BEEN RENDERED") || errorString.includes("DUPE")) {
            finalMessage = "Security check expired. Please go back and try to login in again.";
        } else if (errorString.includes("INVALID-VERIFICATION-CODE") || errorString.includes("INVALID_CODE")) {
            finalMessage = "The security code you entered is incorrect.";
        } else if (errorString.includes("TOO-MANY-REQUESTS") || errorString.includes("TOO_MANY_ATTEMPTS")) {
            finalMessage = "Too many attempts. Please wait a few minutes and try again.";
        } else {
            finalMessage = "Authentication failed. Please try again.";
        }

        if (onSpecificError) {
            onSpecificError(finalMessage);
        } else {
            toast.error(finalMessage);
        }
    };


    // ============================================
    // CORE FUNCTIONS: MFA SETUP
    // ============================================

    // Step 1 of Enrolling: Fire off an SMS securely and retrieve the global verification Tracking ID
    const enrollPhoneSendOtp = async (phone, containerId, recaptchaRef, onSessionExpired) => {
        try {
            const finalPhone = phone.startsWith('+') ? phone : `+91${phone}`;

            const recaptchaVerifier = getRecaptchaVerifier(containerId, recaptchaRef);
            if (!recaptchaVerifier) throw new Error("reCAPTCHA not initialized.");

            const user = auth.currentUser;
            if (!user) {
                const sessionErr = new Error("auth/session-expired");
                sessionErr.code = "auth/session-expired";
                throw sessionErr;
            }

            // Grab an admin-level ticket to modify user profile security factors
            const multiFactorSession = await multiFactor(user).getSession();
            const phoneInfoOptions = { phoneNumber: finalPhone, session: multiFactorSession };

            const phoneAuthProvider = new PhoneAuthProvider(auth);
            const verificationId = await phoneAuthProvider.verifyPhoneNumber(phoneInfoOptions, recaptchaVerifier);

            toast.success("OTP sent to " + finalPhone);
            // Return the generated Tracking ID back to the main file so it can store it in React State
            return verificationId;

        } catch (err) {
            handleMfaError(err, onSessionExpired);
            resetRecaptchaVerifier(recaptchaRef, containerId);
            return null;
        }
    };

    // Step 2 of Enrolling: Pair up the 6 digits the user types with the Tracking ID to seal the factor in
    const enrollPhoneVerifyOtp = async (verificationId, otpCode, recaptchaRef, containerId, onSessionExpired, onSpecificError) => {
        try {
            const cred = PhoneAuthProvider.credential(verificationId, otpCode);
            const multiFactorAssertion = PhoneMultiFactorGenerator.assertion(cred);

            const user = auth.currentUser;
            if (!user) {
                const sessionErr = new Error("auth/session-expired");
                sessionErr.code = "auth/session-expired";
                throw sessionErr;
            }

            // Officially append "Primary Phone" to the secure Firebase database
            await multiFactor(user).enroll(multiFactorAssertion, "Primary Phone");

            resetRecaptchaVerifier(recaptchaRef, containerId);
            toast.success("MFA enrolled successfully!");
            return true;

        } catch (err) {
            handleMfaError(err, onSessionExpired, onSpecificError);
            return false;
        }
    };


    // ============================================
    // CORE FUNCTIONS: MFA EXISTING USERS
    // ============================================

    // Step 1 of Login Challenge: Text the already established phone number on record
    const verifyChallengeSendOtp = async (mfaResolver, containerId, recaptchaRef, onSessionExpired) => {
        try {
            // Find the user's phone record hidden in the login-hostage 'Resolver' object provided by Firebase Auth
            const phoneHint = mfaResolver.hints.find(h => h.factorId === PhoneMultiFactorGenerator.FACTOR_ID);
            if (!phoneHint) throw new Error("No phone number enrolled for this user.");

            const recaptchaVerifier = getRecaptchaVerifier(containerId, recaptchaRef);
            if (!recaptchaVerifier) throw new Error("reCAPTCHA not initialized.");

            const phoneInfoOptions = { multiFactorHint: phoneHint, session: mfaResolver.session };
            const phoneAuthProvider = new PhoneAuthProvider(auth);

            const verificationId = await phoneAuthProvider.verifyPhoneNumber(phoneInfoOptions, recaptchaVerifier);
            toast.success("Security code sent!");
            // Send tracking ID up to Login.js React state
            return verificationId;

        } catch (err) {
            handleMfaError(err, onSessionExpired);
            resetRecaptchaVerifier(recaptchaRef, containerId);
            return null;
        }
    };

    // Step 2 of Login Challenge: Finish logging in explicitly bypassing the hostage 'Resolver' object
    const verifyChallengeVerifyOtp = async (verificationId, otpCode, mfaResolver, onSessionExpired, onSpecificError) => {
        try {
            const cred = PhoneAuthProvider.credential(verificationId, otpCode);
            const multiFactorAssertion = PhoneMultiFactorGenerator.assertion(cred);

            // Allow the user to complete their login securely
            await mfaResolver.resolveSignIn(multiFactorAssertion);
            return true;

        } catch (err) {
            handleMfaError(err, onSessionExpired, onSpecificError);
            return false;
        }
    };

    // Expose these methods to be cleanly imported anywhere
    return {
        enrollPhoneSendOtp,
        enrollPhoneVerifyOtp,
        verifyChallengeSendOtp,
        verifyChallengeVerifyOtp,
        resetRecaptchaVerifier
    };
};

export default useMfa;